import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { useVerification } from '../context/VerificationContext';

const STATUS_LABELS = {
  running:   { text: 'Probíhá', color: 'text-green-600', bg: 'bg-green-100' },
  paused:    { text: 'Pozastaveno', color: 'text-yellow-600', bg: 'bg-yellow-100' },
  completed: { text: 'Dokončeno', color: 'text-blue-600', bg: 'bg-blue-100' },
};

function formatDuration(ms) {
  const s = Math.floor(ms / 1000);
  if (s < 60)    return `${s} s`;
  if (s < 3600)  return `${Math.floor(s / 60)} min ${s % 60} s`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

function estimateRemaining(progress, options) {
  const remaining = (progress.total || 0) - (progress.processed || 0);
  if (remaining <= 0) return null;
  let msPerRecord = 0;
  if (options.verifyIco || options.verifyAddress) msPerRecord += 400;
  if (options.verifySignal) msPerRecord += 1400;
  if (msPerRecord === 0) return null;
  return formatDuration(remaining * msPerRecord);
}

export default function AutoVerificationModal({ onClose, currentUserId, isDarkMode }) {
  const { task, startVerification, pauseVerification, resumeVerification, stopVerification } = useVerification();

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedOwner, setSelectedOwner] = useState('');
  const [options, setOptions] = useState({
    verifyIco:     true,
    verifyAddress: true,
    verifySignal:  false,
  });
  const [confirmStop, setConfirmStop] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/users?userId=${currentUserId || ''}`);
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : (data.users || []);
          setUsers(list.filter(u => u._id && u.name));
        }
      } catch {}
      setLoadingUsers(false);
    };
    load();
  }, [currentUserId]);

  const hasActiveTask = task && task.status !== 'completed';

  const handleStart = () => {
    if (!selectedOwner) return;
    const owner = users.find(u => u._id === selectedOwner);
    startVerification({
      ownerId:   selectedOwner,
      ownerName: owner?.name || selectedOwner,
      options,
    });
  };

  const handleStop = () => {
    if (!confirmStop) { setConfirmStop(true); return; }
    stopVerification();
    setConfirmStop(false);
  };

  const progressPct = task?.progress?.total
    ? Math.round(((task.progress.processed || 0) / task.progress.total) * 100)
    : null;

  const base = isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900';
  const card = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const inputCls = isDarkMode
    ? 'bg-gray-700 border-gray-600 text-white'
    : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className={`rounded-xl shadow-2xl w-full max-w-2xl flex flex-col ${base}`} style={{ maxHeight: '90vh' }}>
        {/* Hlavička */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold">🔄 Automatické ověření firem</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Probíhající úloha */}
          {task && (
            <div className={`rounded-lg border p-4 space-y-3 ${card}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {task.status === 'running' && (
                    <span className="inline-block w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                  )}
                  <span className="font-semibold">
                    {task.ownerName}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_LABELS[task.status]?.bg} ${STATUS_LABELS[task.status]?.color}`}>
                    {STATUS_LABELS[task.status]?.text || task.status}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  Spuštěno: {task.startedAt ? new Date(task.startedAt).toLocaleString('cs-CZ') : '–'}
                </div>
              </div>

              {/* Možnosti */}
              <div className="flex gap-3 text-sm flex-wrap">
                {task.options?.verifyIco     && <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">✓ IČO status</span>}
                {task.options?.verifyAddress && <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200">✓ Adresa + kraj</span>}
                {task.options?.verifySignal  && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200">✓ Signál LTE</span>}
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>
                    {task.progress?.processed || 0} / {task.progress?.total ? task.progress.total.toLocaleString('cs-CZ') : '?'} firem
                    {progressPct !== null && ` (${progressPct} %)`}
                  </span>
                  {task.status === 'running' && task.options && task.progress?.total > 0 && (
                    <span className="text-gray-400">
                      zbývá ~{estimateRemaining(task.progress, task.options) || '?'}
                    </span>
                  )}
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                  <div
                    className="bg-blue-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${progressPct ?? 0}%` }}
                  />
                </div>
              </div>

              {/* Aktuální firma */}
              {task.status === 'running' && task.currentCompany && (
                <div className="text-sm text-gray-500 truncate">
                  Zpracovávám: <strong>{task.currentCompany}</strong>
                </div>
              )}

              {/* Statistiky */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                {task.options?.verifyIco && (
                  <div className={`rounded p-2 ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <div className="font-semibold mb-1 text-blue-600">IČO status</div>
                    <div className="text-green-600">✓ {task.progress?.icoSuccess || 0}</div>
                    <div className="text-red-500">✗ {task.progress?.icoFailed || 0}</div>
                  </div>
                )}
                {task.options?.verifyAddress && (
                  <div className={`rounded p-2 ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <div className="font-semibold mb-1 text-indigo-600">Adresa + kraj</div>
                    <div className="text-green-600">✓ {task.progress?.addressSuccess || 0}</div>
                    <div className="text-red-500">✗ {task.progress?.addressFailed || 0}</div>
                  </div>
                )}
                {task.options?.verifySignal && (
                  <div className={`rounded p-2 ${isDarkMode ? 'bg-gray-700' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <div className="font-semibold mb-1 text-red-600">Signál LTE</div>
                    <div className="text-green-600">✓ {task.progress?.signalSuccess || 0}</div>
                    <div className="text-red-500">✗ {task.progress?.signalFailed || 0}</div>
                  </div>
                )}
              </div>

              {task.status === 'completed' && (
                <div className="text-sm text-blue-600 font-medium">
                  ✅ Ověření dokončeno! Dokončeno: {task.completedAt ? new Date(task.completedAt).toLocaleString('cs-CZ') : ''}
                </div>
              )}

              {/* Ovládání */}
              <div className="flex gap-2 flex-wrap pt-1">
                {task.status === 'running' && (
                  <button
                    onClick={pauseVerification}
                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded font-medium text-sm"
                  >
                    ⏸ Pozastavit
                  </button>
                )}
                {task.status === 'paused' && (
                  <button
                    onClick={resumeVerification}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium text-sm"
                  >
                    ▶ Pokračovat
                  </button>
                )}
                {task.status === 'completed' && (
                  <button
                    onClick={stopVerification}
                    className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-medium text-sm"
                  >
                    🗑 Smazat výsledek
                  </button>
                )}
                {task.status !== 'completed' && (
                  <button
                    onClick={handleStop}
                    className={`px-4 py-2 rounded font-medium text-sm ${confirmStop ? 'bg-red-600 hover:bg-red-700' : 'bg-red-100 hover:bg-red-200 text-red-700'} text-white`}
                  >
                    {confirmStop ? '⚠️ Potvrdit zastavení' : '⏹ Zastavit'}
                  </button>
                )}
                {confirmStop && (
                  <button
                    onClick={() => setConfirmStop(false)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded font-medium text-sm"
                  >
                    Zrušit
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Nová úloha (skrytá, pokud probíhá) */}
          {!hasActiveTask && (
            <div className={`rounded-lg border p-4 space-y-4 ${card}`}>
              <h3 className="font-semibold text-base">Nové ověření</h3>

              {/* Výběr vlastníka */}
              <div>
                <label className="block text-sm font-medium mb-1">Vlastník firem</label>
                {loadingUsers ? (
                  <div className="text-sm text-gray-400">Načítám uživatele…</div>
                ) : (
                  <select
                    value={selectedOwner}
                    onChange={e => setSelectedOwner(e.target.value)}
                    className={`w-full rounded border px-3 py-2 text-sm ${inputCls}`}
                  >
                    <option value="">— vyberte vlastníka —</option>
                    {users.map(u => (
                      <option key={u._id} value={u._id}>{u.name} {u.email ? `(${u.email})` : ''}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Možnosti */}
              <div>
                <label className="block text-sm font-medium mb-2">Co ověřit</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options.verifyIco}
                      onChange={e => setOptions(o => ({ ...o, verifyIco: e.target.checked }))}
                      className="mt-0.5 accent-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium">IČO status (ARES)</div>
                      <div className="text-xs text-gray-500">Aktualizuje pole „Stav IČO" — AKTIVNÍ / ZANIKLA / POZASTAVENO. ~0,4 s/firma.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options.verifyAddress}
                      onChange={e => setOptions(o => ({ ...o, verifyAddress: e.target.checked }))}
                      className="mt-0.5 accent-indigo-600"
                    />
                    <div>
                      <div className="text-sm font-medium">Adresa + kraj (ARES)</div>
                      <div className="text-xs text-gray-500">Přepíše adresu sídla a kraj z rejstříku. Kombinuje se s IČO — bez dalšího zpomalení.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={options.verifySignal}
                      onChange={e => setOptions(o => ({ ...o, verifySignal: e.target.checked }))}
                      className="mt-0.5 accent-red-600"
                    />
                    <div>
                      <div className="text-sm font-medium">Signál LTE (ČTÚ VPortal)</div>
                      <div className="text-xs text-gray-500">
                        Geocoding + WMS dotaz na pokrytí. ~1,5 s/firma. Pro 300 000 firem ≈ 5 dní.{' '}
                        <span className="text-orange-500 font-medium">Doporučeno jen pro menší sady.</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Odhad času */}
              {selectedOwner && (options.verifyIco || options.verifyAddress || options.verifySignal) && (
                <div className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 rounded p-2">
                  Odhad vychází z počtu firem vlastníka. Přesný čas zobrazíme po načtení dat.
                </div>
              )}

              <button
                onClick={handleStart}
                disabled={!selectedOwner || (!options.verifyIco && !options.verifyAddress && !options.verifySignal)}
                className="w-full py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded font-semibold transition text-sm"
              >
                ▶ Spustit ověření na pozadí
              </button>
            </div>
          )}

          {hasActiveTask && (
            <p className="text-sm text-gray-500 text-center">
              Chcete-li spustit nové ověření, nejprve zastavte nebo počkejte na dokončení aktuálního.
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded font-medium text-sm"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
